import React, { useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
  Gauge, Newspaper, Images, UsersRound, Inbox, CreditCard, ScrollText, LogOut, Menu, X, ShieldCheck, Loader2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { CmsBaseContext } from "../portal/cms/cmsBase";
import { useNoIndex } from "./useNoIndex";

const LINKS = [
  { to: "/control-panel", label: "Overview", icon: Gauge, tid: "overview", end: true },
  { to: "/control-panel/content", label: "Content", icon: Newspaper, tid: "content" },
  { to: "/control-panel/media", label: "Media Library", icon: Images, tid: "media" },
  { to: "/control-panel/users", label: "Users & Roles", icon: UsersRound, tid: "users" },
  { to: "/control-panel/inquiries", label: "Inquiries", icon: Inbox, tid: "inquiries" },
  { to: "/control-panel/dues", label: "Dues", icon: CreditCard, tid: "dues" },
  { to: "/control-panel/activity", label: "Activity Log", icon: ScrollText, tid: "activity" },
];

export default function ControlLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useNoIndex();

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-[#0B1530]"><Loader2 className="animate-spin text-gold" /></div>;
  if (!user?.is_platform_admin) return <Navigate to="/control-panel/login" replace />;

  const signOut = async () => { await logout(); nav("/control-panel/login", { replace: true }); };

  return (
    <div className="flex min-h-screen bg-[#F4F1EA]" data-testid="control-layout">
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-72 flex-col bg-[#0B1530] transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-3 border-b border-white/10 px-6 py-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gold/40 bg-gold/10 text-gold"><ShieldCheck size={20} /></div>
          <div>
            <p className="font-display text-xl font-semibold text-parchment">Control Panel</p>
            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold/70">Zetland No. 508</p>
          </div>
          <button className="ml-auto text-parchment/60 lg:hidden" onClick={() => setOpen(false)} aria-label="Close menu"><X size={20} /></button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-4">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} data-testid={`control-nav-${l.tid}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 font-sans text-sm transition-colors ${isActive ? "bg-gold font-semibold text-[#0B1530]" : "text-parchment/70 hover:bg-white/5 hover:text-gold"}`}>
              <l.icon size={18} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-5">
          <p className="truncate font-sans text-sm font-semibold text-parchment" data-testid="control-user-name">{user.name}</p>
          <p className="truncate font-sans text-xs text-parchment/50">{user.email} · Platform Admin</p>
          <button onClick={signOut} className="mt-4 flex items-center gap-2 font-sans text-xs font-semibold uppercase tracking-widest text-gold/80 hover:text-gold" data-testid="control-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
      <main className="min-w-0 flex-1">
        <div className="flex items-center gap-3 border-b border-sapphire-deep/10 bg-white/70 px-6 py-4 backdrop-blur lg:hidden">
          <button onClick={() => setOpen(true)} className="text-sapphire-deep" data-testid="control-menu-btn" aria-label="Open menu"><Menu size={22} /></button>
          <p className="font-display text-lg font-semibold text-sapphire-deep">Control Panel</p>
        </div>
        <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:px-12">
          <CmsBaseContext.Provider value="/control-panel"><Outlet /></CmsBaseContext.Provider>
        </div>
      </main>
    </div>
  );
}
